// 자체 백엔드(Express + MySQL) API 클라이언트
const API_BASE = import.meta.env.VITE_PUBLIC_API_URL || "/api";

const TOKEN_KEY = "ugongs_admin_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return Boolean(getToken());
}

export function resolveImageUrl(url: string): string {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  if (url.startsWith("/")) {
    const apiBase = import.meta.env.VITE_PUBLIC_API_URL || "";
    if (apiBase) {
      try {
        const { origin } = new URL(apiBase);
        return `${origin}${url}`;
      } catch {
        return url;
      }
    }
  }
  return url;
}

async function request<T>(path: string, options: { method?: string; body?: string; headers?: Record<string, string> } = {}): Promise<T> {
  const token = getToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method: options.method || "GET",
    headers,
    body: options.body,
  });

  const responseText = await response.text();
  let data: unknown = null;
  try {
    data = JSON.parse(responseText);
  } catch {
    data = responseText;
  }

  if (!response.ok) {
    const msg =
      (data as { error?: string } | null)?.error ||
      `요청 처리에 실패했습니다. (${response.status})`;
    throw new Error(msg);
  }

  return data as T;
}

export interface AuthUser {
  id: number;
  username: string;
}

export const authApi = {
  login: (username: string, password: string) =>
    request<{ token: string; username: string }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  me: () => request<AuthUser>("/auth/me"),
  logout: () => request<{ success: boolean }>("/auth/logout", { method: "POST" }),
};

// ---------- 장비 ----------
export interface EquipmentItem {
  id: number;
  name: string;
  manufacturer: string;
  model: string;
  engine: string;
  power_kw: string;
  power_ps: string;
  max_blades: string;
  max_input_diameter: string;
  screens: string;
  mobility: string;
  image: string;
  description: string;
}

export const equipmentApi = {
  list: () => request<{ data: EquipmentItem[] }>("/equipment"),
  create: (payload: Partial<EquipmentItem>) =>
    request<{ success: boolean; id: number }>("/equipment", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  update: (id: number, payload: Partial<EquipmentItem>) =>
    request<{ success: boolean }>(`/equipment/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  remove: (id: number) =>
    request<{ success: boolean }>(`/equipment/${id}`, { method: "DELETE" }),
  uploadImage: (file: File) => uploadFile("/equipment/upload", file),
};

// ---------- 갤러리 ----------
export interface GalleryPost {
  id: number;
  title: string;
  category: string;
  content: string;
  image: string;
  views: number;
  author: string;
  created_at: string;
}

export const galleryApi = {
  list: (category?: string) => {
    const q = category && category !== "전체" ? `?category=${encodeURIComponent(category)}` : "";
    return request<{ data: GalleryPost[] }>(`/gallery${q}`);
  },
  create: (payload: Partial<GalleryPost>) =>
    request<{ success: boolean; id: number }>("/gallery", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  update: (id: number, payload: Partial<GalleryPost>) =>
    request<{ success: boolean }>(`/gallery/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  remove: (id: number) =>
    request<{ success: boolean }>(`/gallery/${id}`, { method: "DELETE" }),
  incrementView: (id: number) =>
    request<{ success: boolean }>(`/gallery/${id}/view`, { method: "POST" }),
  uploadImage: (file: File) => uploadFile("/gallery/upload", file),
};

// ---------- 문의 ----------
export interface Inquiry {
  id: number;
  name: string;
  phone: string;
  email: string;
  company: string;
  service: string;
  screen: string;
  site: string;
  message: string;
  status: string;
  created_at: string;
}

export const inquiryApi = {
  create: (payload: Partial<Inquiry>) =>
    request<{ success: boolean; id: number }>("/inquiries", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  list: (page: number, pageSize: number, status?: string) => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    if (status && status !== "전체") params.set("status", status);
    return request<{ data: Inquiry[]; total: number }>(
      `/inquiries?${params.toString()}`
    );
  },
  updateStatus: (id: number, status: string) =>
    request<{ success: boolean }>(`/inquiries/${id}/status`, {
      method: "PUT",
      body: JSON.stringify({ status }),
    }),
  remove: (id: number) =>
    request<{ success: boolean }>(`/inquiries/${id}`, { method: "DELETE" }),
  bulkRemove: (status?: string) => {
    const q = status && status !== "전체" ? `?status=${encodeURIComponent(status)}` : "";
    return request<{ success: boolean }>(`/inquiries${q}`, { method: "DELETE" });
  },
};

// ---------- 통계 ----------
export interface RecentInquiry {
  id: number;
  name: string;
  service: string;
  status: string;
  created_at: string;
}

export interface DashboardStats {
  equipmentCount: number;
  galleryCount: number;
  inquiryCount: number;
  inquiryReceived: number;
  inquiryProcessing: number;
  inquiryDone: number;
  recentInquiries: RecentInquiry[];
}

export const statsApi = {
  get: () => request<{ data: DashboardStats }>("/stats"),
};

// ---------- 이미지 업로드 공용 ----------
async function uploadFile(path: string, file: File): Promise<string> {
  const token = getToken();
  const formData = new FormData();
  formData.append("image", file);

  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers,
    body: formData,
  });

  const responseText = await response.text();
  let data: { url?: string; error?: string } | null = null;
  try {
    data = JSON.parse(responseText);
  } catch {
    data = null;
  }

  if (!response.ok || !data?.url) {
    throw new Error(data?.error || "이미지 업로드에 실패했습니다.");
  }

  return data.url;
}